import { Box, Button, Grid } from '@mui/material'
import React, { useEffect, useState } from 'react'
import EmptyTableRow from 'src/Component/EmptyTableRow'
import PaginatedTable, { HeadCellI, SortOrderI } from 'src/Component/PaginatedTable'
import DataRow from './DataRow'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'src/store'
import { startPageLoader, stopPageLoader } from 'src/store/features/common/commonSlice'
import { errorToast, successToast } from 'src/utils/toastService'
import SearchInput from 'src/Component/SearchInput'
import { useGetBranchGymsListQuery, useDeleteBranchGymMutation } from 'src/api/branchGymApi'
import AddBranchGymModal from './AddBranchGymModal'
import ConfirmationBox from 'src/Component/ConfirmationBox'
import ViewBranchGymDetailsModal from './ViewBranchGymDetailsModal'
import EditBranchGymModal from './EditBranchGymModal'
import TabComponent from 'src/Component/TabComponent'

const headCells: HeadCellI[] = [
  {
    id: 'id',
    isCheckBox: true,
    sortable: false,
    label: 'checkbox',
  },
  {
    id: 'name',
    sortable: true,
    label: 'Gym Name',
  },
  {
    id: 'address',
    sortable: true,
    label: 'Address',
  },
  {
    id: 'latitude',
    sortable: true,
    label: 'Latitude',
  },
  {
    id: 'longitude',
    sortable: true,
    label: 'Longitude',
  },
  {
    id: 'rating',
    sortable: true,
    label: 'Rating',
  },
  {
    id: 'id',
    sortable: false,
    label: 'Action',
    align: 'center',
  },
]

type BranchGymProps = {
  tabs: string[]
  activeTab: number
  setActiveTab: (activeTab: number) => void
}
const BranchGym = ({ activeTab, setActiveTab, tabs }: BranchGymProps) => {
  const [page, setPage] = useState<number>(1)
  const [sortOrder, setSortOrder] = useState<SortOrderI>('asc')
  const [sortOrderBy, setSortOrderBy] = useState<string>('')
  const [openAddNewModal, setOpenAddNewModal] = useState(false)
  const [BranchGymDetailsModal, setBranchGymDetailsModal] = useState({
    id: 0,
    open: false,
  })

  const [BranchGymDeleteConfirmation, setBranchGymDeleteConfirmation] = useState({
    id: 0,
    open: false,
  })

  const [editBranchGymModal, setEditBranchGymModal] = useState({
    id: 0,
    open: false,
  })

  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [search, setSearch] = useState<string>('')

  // hooks
  const {
    data: BRANCH_GYM_DATA_RESPONSE,
    isFetching,
    refetch,
  } = useGetBranchGymsListQuery(
    {
      search,
      page,
      pageSize: 10,
      sortBy: (() => {
        if (sortOrder === 'asc') {
          return sortOrderBy
        } else {
          return `-${sortOrderBy}`
        }
      })(),
    },
    { refetchOnMountOrArgChange: true },
  )
  const [deleteBranchGym, { isLoading }] = useDeleteBranchGymMutation()
  const dispatch = useDispatch<AppDispatch>()

  // functions
  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handleSorting = (_sortOrderBy: string) => {
    setSortOrder((prevState) => {
      return prevState === 'asc' ? 'desc' : 'asc'
    })
    setSortOrderBy(_sortOrderBy)
  }

  const handleBranchGymDetailsModalClose = () => {
    setBranchGymDetailsModal({
      id: 0,
      open: false,
    })
  }

  const handleViewDetailsClick = (id: number) => {
    setBranchGymDetailsModal({
      id: id,
      open: true,
    })
  }

  const handleCloseDeleteConfirmation = () => {
    setBranchGymDeleteConfirmation({
      id: 0,
      open: false,
    })
  }

  const handleOpenDeleteConfirmation = (id: number) => {
    setBranchGymDeleteConfirmation({
      id: id,
      open: true,
    })
  }

  const handleEditBranchGymModalClose = () => {
    setEditBranchGymModal({
      id: 0,
      open: false,
    })
  }

  const handleEditBranchGymClick = (id: number) => {
    setEditBranchGymModal({
      id: id,
      open: true,
    })
  }

  const handleDeleteBranchGym = () => {
    deleteBranchGym(BranchGymDeleteConfirmation.id)
      .unwrap()
      .then((res) => {
        console.log(res)
        successToast('Branch Gym deleted successfully.')
        handleEditBranchGymModalClose()
        refetch()
      })
      .catch((err) => {
        console.log(err)
        errorToast('Unable to delete Branch Gym.')
      })
      .finally(() => {
        handleCloseDeleteConfirmation()
      })
  }

  const handleSelectGym = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds((prevState) => {
        return prevState.filter((gymId) => gymId !== id)
      })
    } else {
      setSelectedIds((prevState) => {
        return [...prevState, id]
      })
    }
  }

  const handleSelectAllClick = (checked: boolean) => {
    if (checked && BRANCH_GYM_DATA_RESPONSE?.results.length) {
      setSelectedIds(BRANCH_GYM_DATA_RESPONSE.results.map((gym) => gym.id))
    } else {
      setSelectedIds([])
    }
  }

  const handleDeleteSelected = () => {
    console.log(selectedIds)
    Promise.all(selectedIds.map((id) => deleteBranchGym(id).unwrap()))
      .then((res) => {
        console.log(res)
        successToast('Selected gyms deleted successfully.')
        refetch()
      })
      .catch((err) => {
        console.log(err)
        errorToast('Unable to  delete selected gyms.')
      })
  }

  const handleSearch = (value: string) => {
    setPage(1)
    setSearch(value)
  }

  const onAddBranchGymSuccessCallback = () => {
    setOpenAddNewModal(false)
    refetch()
  }

  const onEditBranchGymSuccessCallback = () => {
    handleEditBranchGymModalClose()
    refetch()
  }

  useEffect(() => {
    if (isFetching || isLoading) {
      dispatch(startPageLoader())
    } else {
      dispatch(stopPageLoader())
    }
  }, [isFetching, isLoading])

  useEffect(() => {
    setSelectedIds([])
  }, [BRANCH_GYM_DATA_RESPONSE])

  return (
    <Box className='pageContainer'>
      <Grid container>
        <Grid md={8} sm={12} xs={12}>
          {' '}
        </Grid>
        <Grid md={4} sm={12} xs={12}>
          <Box>
            <SearchInput hasDebounce onChange={handleSearch} />
          </Box>
        </Grid>
      </Grid>

      <Grid mt={10} container justifyContent='space-between'>
        <Grid item>
          {' '}
          <TabComponent options={tabs} activeIndex={activeTab} onTabIndexChange={setActiveTab} />
        </Grid>
        <Grid item>
          {' '}
          <Button
            variant='contained'
            color='primary'
            type='button'
            onClick={() => setOpenAddNewModal(true)}
          >
            ADD NEW
          </Button>
        </Grid>
      </Grid>

      <Box mt={2}>
        {BRANCH_GYM_DATA_RESPONSE && (
          <PaginatedTable
            title='Branch Gyms'
            headCells={headCells}
            currentPage={page}
            onPageChange={handlePageChange}
            totalPages={Math.ceil(BRANCH_GYM_DATA_RESPONSE?.count / 10)}
            onSortClick={handleSorting}
            onSelectAllClick={handleSelectAllClick}
            isAllSelected={BRANCH_GYM_DATA_RESPONSE.results.length === selectedIds.length}
            numSelected={selectedIds.length}
            onDeleteSelected={handleDeleteSelected}
          >
            <>
              {BRANCH_GYM_DATA_RESPONSE?.results?.length === 0 && (
                <EmptyTableRow cellCount={headCells.length} />
              )}
              {BRANCH_GYM_DATA_RESPONSE?.results?.map((data) => {
                return (
                  <DataRow
                    key={data.id}
                    id={data.id}
                    name={data.name}
                    address={data.address}
                    latitude={data.latitude}
                    longitude={data.longitude}
                    rating={data.rating}
                    selected={selectedIds.includes(data.id)}
                    onViewDetailsClick={handleViewDetailsClick}
                    onDeleteClick={handleOpenDeleteConfirmation}
                    onSelect={handleSelectGym}
                    onEditClick={handleEditBranchGymClick}
                  />
                )
              })}
            </>
          </PaginatedTable>
        )}
      </Box>

      <AddBranchGymModal
        open={openAddNewModal}
        onClose={() => setOpenAddNewModal(false)}
        successCallback={onAddBranchGymSuccessCallback}
      />
      {!!BranchGymDetailsModal.id && (
        <ViewBranchGymDetailsModal
          id={BranchGymDetailsModal.id}
          open={BranchGymDetailsModal.open}
          onClose={handleBranchGymDetailsModalClose}
        />
      )}

      <ConfirmationBox
        title='Are you sure you want to Delete this gym.'
        open={BranchGymDeleteConfirmation.open}
        onReject={handleCloseDeleteConfirmation}
        onAccept={handleDeleteBranchGym}
      />

      {!!editBranchGymModal.id && (
        <EditBranchGymModal
          id={editBranchGymModal.id}
          open={editBranchGymModal.open}
          onClose={handleEditBranchGymModalClose}
          successCallback={onEditBranchGymSuccessCallback}
          onDeleteClick={handleOpenDeleteConfirmation}
        />
      )}
    </Box>
  )
}

export default BranchGym
